import React from 'react';
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, FormControl, InputLabel, MenuItem, FormControlLabel, Switch, IconButton,
} from '@mui/material';
import { Assessment as AssessmentIcon, Close as CloseIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import BuscarPacienteModal from '../BuscarPacienteModal';
import DetalleConsumoCliente from '../DetalleConsumoCliente';
import ImportarDteModal from '../ImportarDteModal';
import CalculadoraCambioModal from '../CalculadoraCambioModal';

/**
 * Agrupa todos los diálogos (modales) de la vista de detalle de Albarán.
 * Se mantienen juntos porque comparten la naturaleza de "acciones
 * secundarias fuera del flujo principal" y así se evita saturar el
 * contenedor con marcado de <Dialog> repetido.
 */
const AlbaranModales = ({
  esNuevo,
  selectedPaciente,

  // Calculadora de cambio
  openCambioModal,
  setOpenCambioModal,
  saldoPendiente,
  onConfirmarCambio,

  // Marcar Facturado
  openFacturarModal,
  setOpenFacturarModal,
  referenciaFactura,
  setReferenciaFactura,
  onMarcarFacturado,

  // Importar DTE
  openImportDteModal,
  setOpenImportDteModal,
  idSucursalDte,
  idPacienteDte,
  idClienteDte,
  onImportDteExito,

  // Anular Albarán
  openAnularModal,
  setOpenAnularModal,
  motivoAnulacionAlbaran,
  setMotivoAnulacionAlbaran,
  onAnularAlbaran,

  // Imprimir PDF
  openPrintModal,
  setOpenPrintModal,
  tipoDtePrint,
  setTipoDtePrint,
  aplicaRetencionPrint,
  setAplicaRetencionPrint,
  onConfirmarImprimir,

  // Cerrar Albarán
  openCerrarModal,
  setOpenCerrarModal,
  observacionesCierre,
  setObservacionesCierre,
  onConfirmarCerrar,

  // Historial de Consumo
  openConsumoModal,
  onCerrarConsumo,

  // Anular Abono
  openDeleteModal,
  setOpenDeleteModal,
  motivoEliminacion,
  setMotivoEliminacion,
  onConfirmarAnulacionAbono,

  // Buscar Paciente
  openPacienteModal,
  setOpenPacienteModal,
  onSeleccionarPacienteModal,
}) => {
  return (
    <>
      <CalculadoraCambioModal
        open={openCambioModal}
        onClose={() => setOpenCambioModal(false)}
        totalPagar={saldoPendiente}
        onConfirmarPago={onConfirmarCambio}
      />

      {/* Modal Marcar como Facturado */}
      <Dialog open={openFacturarModal} onClose={() => setOpenFacturarModal(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>Marcar como Facturado</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Ingrese el número de factura o referencia externa del comprobante:
          </Typography>
          <TextField
            fullWidth
            label="Nº Referencia Factura"
            value={referenciaFactura}
            onChange={(e) => setReferenciaFactura(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenFacturarModal(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={onMarcarFacturado}
            disabled={!referenciaFactura.trim()}
          >
            Confirmar Factura
          </Button>
        </DialogActions>
      </Dialog>

      <ImportarDteModal
        open={openImportDteModal}
        onClose={() => setOpenImportDteModal(false)}
        idSucursal={idSucursalDte}
        idPet={idPacienteDte}
        idCliente={idClienteDte}
        onSuccess={(numControl, codGeneracion) => onImportDteExito(numControl, codGeneracion)}
      />

      <Dialog open={openAnularModal} onClose={() => setOpenAnularModal(false)} fullWidth maxWidth="xs">
        <DialogTitle>Anular Albarán</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            ¿Está seguro de anular este albarán? Escriba el motivo de la anulación:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Motivo de anulación"
            value={motivoAnulacionAlbaran}
            onChange={(e) => setMotivoAnulacionAlbaran(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAnularModal(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={onAnularAlbaran}
            disabled={!motivoAnulacionAlbaran.trim()}
          >
            Anular Comprobante
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Seleccionar Tipo de Comprobante para PDF */}
      <Dialog open={openPrintModal} onClose={() => setOpenPrintModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Imprimir Albarán</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Seleccione el tipo de comprobante que desea generar:
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Tipo de Comprobante</InputLabel>
            <Select
              value={tipoDtePrint}
              label="Tipo de Comprobante"
              onChange={(e) => setTipoDtePrint(e.target.value)}
            >
              <MenuItem value="01">01 - Factura</MenuItem>
              <MenuItem value="03">03 - Comprobante de Crédito Fiscal</MenuItem>
              <MenuItem value="11">11 - Factura de Exportación</MenuItem>
            </Select>
          </FormControl>

          {tipoDtePrint === '03' && (
            <FormControlLabel
              control={
                <Switch
                  checked={aplicaRetencionPrint}
                  onChange={(e) => setAplicaRetencionPrint(e.target.checked)}
                  color="warning"
                />
              }
              label="Aplicar Retención 1% (Gran Contribuyente)"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenPrintModal(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            onClick={onConfirmarImprimir}
          >
            Generar PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Cerrar Albarán */}
      <Dialog open={openCerrarModal} onClose={() => setOpenCerrarModal(false)} fullWidth maxWidth="xs">
        <DialogTitle>Cerrar Albarán</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            ¿Deseas cerrar este albarán? Una vez cerrado no se podrán agregar o modificar más ítems.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Observaciones de Cierre (Opcional)"
            value={observacionesCierre}
            onChange={(e) => setObservacionesCierre(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCerrarModal(false)}>Cancelar</Button>
          <Button color="warning" variant="contained" onClick={onConfirmarCerrar}>
            Confirmar Cierre
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Historial Consumo */}
      <Dialog open={openConsumoModal} onClose={onCerrarConsumo} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Historial de Consumo - {selectedPaciente?.nombre_cliente || selectedPaciente?.nombre || 'Cliente'}
            </Typography>
          </Box>
          <IconButton onClick={onCerrarConsumo} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedPaciente ? (
            <DetalleConsumoCliente idCliente={selectedPaciente.id_cliente || selectedPaciente.cliente_id} />
          ) : (
            <Typography color="text.secondary">No se ha seleccionado ningún cliente.</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onCerrarConsumo} variant="contained" color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Anulación de Abono */}
      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} fullWidth maxWidth="xs">
        <DialogTitle>Anular Abono</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Por favor, especifica el motivo por el cual deseas anular este abono:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Motivo de anulación"
            value={motivoEliminacion}
            onChange={(e) => setMotivoEliminacion(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={onConfirmarAnulacionAbono}
            disabled={!motivoEliminacion.trim()}
          >
            Confirmar Anulación
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Búsqueda Paciente */}
      <BuscarPacienteModal
        open={openPacienteModal}
        handleClose={() => setOpenPacienteModal(false)}
        onSelectPacienteFromModal={onSeleccionarPacienteModal}
      />
    </>
  );
};

export default AlbaranModales;
